import React, { useCallback, useEffect } from "react";
import AddPartForm from "@/components/AddPartForm";
import { motion } from "framer-motion";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "../components/ui/use-toast";
import { fetchPartById } from "../store/useParts/usePartsThunk";
import { clearSelectedPart } from "../store/useParts/usePartsSlice";
import { Button } from "@/components/ui/button";
import { getSellerDashboardStatsByUserId } from "../store/dashboard/dashboardActions";
import { usePartsQuota } from "../hooks/usePartsQuota";
import { ArrowLeft, Loader2, LogIn, UserX } from "lucide-react";
import { openAuthDialog } from "../store/auth/authSlice";
import { manualUnsubscribe } from "../store/dashboard/dashboardSlice";
import { fetchCategoriesAndMakes } from "../store/parts/partsAction";

const AddPartPage = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();
  const itemId = searchParams.get("item");

  const {
    isLoading: isAuthLoading,
    isAuthenticated,
    user,
    currentSubscription,
  } = useSelector((state) => state.auth);
  const { selectedPart, fetchLoading } = useSelector((state) => state.useParts);
  const { unsubscribe } = useSelector((state) => state.dashboard);
  const { canAddParts, partsLimit, totalParts } = usePartsQuota();

  // Cleanup subscription on unmount
  useEffect(() => {
    return () => {
      if (unsubscribe) {
        dispatch(manualUnsubscribe());
      }
    };
  }, [dispatch, unsubscribe]);

  // fetch dashboard stats when form mounts
  useEffect(() => {
    if (user?.uid && user?.role === "seller") {
      dispatch(getSellerDashboardStatsByUserId(user.uid));
    }
  }, [dispatch, user?.uid]);

  useEffect(() => {
    if (isAuthLoading) return;
    if (!isAuthenticated) {
      toast({
        title: "Authentication Required",
        description: "Please log in or sign up to access this page.",
        variant: "destructive",
        duration: 5000,
      });
      navigate("/");
      dispatch(
        openAuthDialog({
          showLoginFirstOrOnSuccess: true,
          redirectPage: window.location.pathname,
          onSuccessfulAuth: () => navigate("/dashboard", { replace: true }),
          defaultTabOverride: "login",
        }),
      );
      return;
    }

    if (user?.role !== "seller") {
      toast({
        title: "Access Denied",
        description:
          "This page is for sellers only. Redirecting to your profile...",
        variant: "destructive",
        duration: 3000,
      });
      navigate(user.role === "buyer" ? "/profile" : "/");
      return;
    }

    // Allow through if they have an active paid subscription OR are on the free plan (no subscription yet).
    const isActivePaidPlan = currentSubscription?.status === "active";
    const isFreePlan = user?.role === "seller" && !currentSubscription;
    if (!isActivePaidPlan && !isFreePlan) {
      toast({
        title: "Membership Required",
        description:
          "Please select a membership plan to access your dashboard.",
        variant: "destructive",
        duration: 5000,
      });
      navigate("/list-inventory");
      return;
    }

    if (!canAddParts) {
      toast({
        variant: "destructive",
        title: "Access Denied",
        description: `You do not have an active subscription that allows adding inventory. ${
          partsLimit === "unlimited"
            ? "Please subscribe to continue."
            : `Your current plan allows up to ${partsLimit} parts. You already have ${totalParts}.`
        } Please subscribe or upgrade your plan to continue.`,
        duration: 5000,
      });
      navigate("/list-inventory");
      return;
    }
  }, [
    isAuthLoading,
    isAuthenticated,
    user,
    canAddParts,
    partsLimit,
    totalParts,
    navigate,
  ]);

  useEffect(() => {
    dispatch(fetchCategoriesAndMakes());
    if (itemId) {
      dispatch(fetchPartById(itemId));
    } else {
      dispatch(clearSelectedPart());
    }
  }, [itemId, dispatch]);

  if (isAuthLoading || fetchLoading) {
    return (
      <div className="flex justify-center items-center min-h-[60vh]">
        <Loader2 className="h-12 w-12 animate-spin text-primary" />
      </div>
    );
  }

  if (!isAuthenticated || user?.role !== "seller" || !canAddParts) {
    return (
      <div className="container mx-auto py-10 px-4 text-center min-h-screen flex flex-col justify-center items-center bg-gradient-to-br from-background to-secondary/5">
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="p-8 rounded-xl bg-card shadow-2xl max-w-md w-full"
        >
          {isAuthenticated ? (
            <UserX className="h-12 w-12 text-destructive mx-auto mb-4" />
          ) : (
            <LogIn className="h-12 w-12 text-primary mx-auto mb-4" />
          )}
          <h1 className="text-2xl font-bold text-foreground mb-3">
            {isAuthenticated ? "Access Denied" : "Authentication Required"}
          </h1>
          <p className="text-muted-foreground">
            {isAuthenticated
              ? "This page is only available for sellers only."
              : "Please log in or sign up to continue."}
          </p>
        </motion.div>
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      transition={{ duration: 0.3 }}
      className="w-full"
    >
      <div className="w-full max-w-4xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4 mb-6">
        <h1 className="text-[21px] sm:text-2xl md:text-3xl font-bold text-center sm:text-left bg-gradient-to-r from-primary to-accent text-transparent bg-clip-text">
          {itemId ? "Update Part in Inventory" : "Add New Part to Inventory"}
        </h1>
        <Button
          variant="outline"
          onClick={() => navigate("/dashboard")}
          className="w-full sm:w-auto"
        >
          <ArrowLeft className="mr-2 h-4 w-4" /> Back to Dashboard
        </Button>
      </div>

      <div className="w-full max-w-4xl mx-auto">
        <AddPartForm selectedPart={selectedPart} />
      </div>
    </motion.div>
  );
};

export default AddPartPage;
